import React, {useEffect, useState, useRef} from 'react';
import {
  Step,
  StepperComponent,
} from '../../common/StepperComponent/StepperComponent';
import NewJobDetailHeader from './new-job-detail-header/NewJobDetailHeader';
import ArrButton from '../../../../common/arr-button/ArrButton';
import './NewJobDetail.scss';
import {t} from 'i18next';
import {ReactComponent as Next} from '../../../../../assets/images/icon-right-outline.svg';
import {Box} from '@mui/material';
import JobDetailStep from './job-detail-step/JobDetailStep';
import SourceSelectionStep, {
  SourceSelectionMode,
} from './source-selection-step/SourceSelectionStep';
import IssueCodeTypeJob, {
  IssueCodeTypeJobRef,
} from './issue-coding-type-job/IssueCodeTypeJob';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import {
  AI_REVIEW_JOB_TYPE,
  PROTOCOL_INTELLIGENCE_FIELDS,
} from '../../constant/AiReviewConstants';
import {ISelectItem} from '../../../../../shared/types';
import {CRITERIA} from './issue-coding-type-header/IssueCodingTypeHeader';
import NewJobConfirmationModal from './new-job-confirmation-modal/NewJobConfirmationModal';
import ContextAndCriteriaStep from './context-and-criteria-step/ContextAndCriteriaStep';
import {useStepValidation} from './useStepValidation';
import {
  IAIReviewProtocolIntelligenceResponse,
  IssueCode,
  JobType,
} from '../../../../../api/types/aiReview.type';
import {v4 as uuidv4} from 'uuid';
import {
  useCreateAIReviewJob,
  useGetAIReviewJobDetails,
  useGetAllProtocolIntelligenceIds,
  useGetProtocolIntelligenceDetails,
  useUpdateAIReviewJob,
} from '../../../../../hooks/ai/review/useReview';
import {
  hideToast,
  resetEditMode,
  resetFormData,
  setCloneJobName,
  setDefineCriteria,
  setFormData,
  setInitialFormData,
  setJobType,
  showToast,
} from '../../../../../redux/slice/aiReviewSlice';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {ToastType} from '../../../../../shared/constants';
import {isEmpty, isEqual} from 'lodash';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';

// Types

export interface JobDetailData {
  jobName: string;
  description: string;
  selectedGroup: ISelectItem | null;
}
export interface ContextAndCriteriaData {
  context: string;
  issueCodes: IssueCode[];
  relevanceCriteria: string;
}

export interface JobDetailFormData {
  jobId?: number;
  jobDetail: JobDetailData;
  contextAndCriteria: ContextAndCriteriaData;
  sourceSelection: number;
  sourceSelectionMode: SourceSelectionMode;
  groundTruth?: string;
}

const newJobDetailSteps: Step[] = [
  {key: 1, label: t('ai.review.newJobDetail.jobDetails'), state: 'current'},
  {
    key: 2,
    label: t('ai.review.newJobDetail.contextAndCriteria'),
    state: 'future',
  },
  {key: 3, label: t('ai.review.newJobDetail.sourceSelection'), state: 'future'},
];

const NewJobDetail = () => {
  const jobType = useSelector((state: RootState) => state.aiReview.jobType);
  const formData = useSelector((state: RootState) => state.aiReview.formData);
  const {toast} = useSelector((state: RootState) => state.aiReview);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {mutate: createAIReviewJob} = useCreateAIReviewJob();
  const {mutate: updateAIReviewJob} = useUpdateAIReviewJob();

  const issueCodeTypeJobRef = useRef<IssueCodeTypeJobRef>(null);
  const [steps, setSteps] = useState<Step[]>(newJobDetailSteps);
  const currentIndex = steps.findIndex((s) => s.state === 'current');
  const [selectedSourceId, setSelectedSourceId] = useState<number | string[]>(
    0
  );
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [showNewJobConfirmationModal, setShowNewJobConfirmationModal] =
    useState(false);
  const [isIssueCodeStepValid, setIsIssueCodeStepValid] = useState(false);
  const [protocolIntelligenceData, setProtocolIntelligenceData] =
    useState<IAIReviewProtocolIntelligenceResponse>();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.has('jobId')
    ? Number(searchParams.get('jobId'))
    : null;

  const [showExitModal, setShowExitModal] = useState(false);
  const store = useStore();

  useEffect(() => {
    if (!jobId) return;

    dispatch(setFormData({jobId}));
  }, [jobId, dispatch]);

  const {data: getJobDetailData} = useGetAIReviewJobDetails(
    {jobId: jobId!},
    !!jobId
  );

  useEffect(() => {
    if (!getJobDetailData) return;
    if (!formData.jobId) return;

    const draftJobType: JobType = getJobDetailData.jobType;
    const draftFormData: JobDetailFormData = {
      jobId: getJobDetailData.jobId,
      jobDetail: {
        jobName: getJobDetailData.jobName,
        description: getJobDetailData.description,
        selectedGroup: {
          id: getJobDetailData.groupID,
        },
      },
      contextAndCriteria: {
        context: getJobDetailData.caseContext,
        issueCodes: getJobDetailData.issueCodes,
        relevanceCriteria: getJobDetailData.relevanceCriteria,
      },
      sourceSelection: getJobDetailData.sourceID,
      sourceSelectionMode: getJobDetailData.sourceType as SourceSelectionMode,
      groundTruth: getJobDetailData.groundTruth ?? '',
    };

    dispatch(setFormData(draftFormData));
    dispatch(setJobType(draftJobType));
    dispatch(setInitialFormData(draftFormData));
  }, [getJobDetailData, formData.jobId, dispatch]);

  const {data: protocolIdData} = useGetAllProtocolIntelligenceIds(
    steps[currentIndex]?.key == 2
  );
  const {data: protocolIntelligenceResponse} =
    useGetProtocolIntelligenceDetails(
      steps[currentIndex]?.key === 2
        ? protocolIdData?.[0]?.protocolId
        : undefined
    );

  useEffect(() => {
    if (steps[currentIndex]?.key == 2 && protocolIntelligenceResponse) {
      setProtocolIntelligenceData(protocolIntelligenceResponse);
    }
  }, [protocolIntelligenceResponse, currentIndex, steps]);

  const isProtocolDisabled =
    !protocolIntelligenceData?.isApproved ||
    !protocolIntelligenceData?.isActive;

  const handleUseSavedProtocol = (field?: string) => {
    dispatch(
      setFormData({
        contextAndCriteria: {
          ...formData.contextAndCriteria,
          ...((!field || field === PROTOCOL_INTELLIGENCE_FIELDS.issueCodes) && {
            issueCodes: (protocolIntelligenceData?.issueCodes ?? []).map(
              (code) => ({
                ...code,
                id: code.id ?? uuidv4(),
              })
            ),
          }),
          ...((!field ||
            field === PROTOCOL_INTELLIGENCE_FIELDS.caseContext) && {
            context: protocolIntelligenceData?.caseContext ?? '',
          }),
          ...((!field ||
            field === PROTOCOL_INTELLIGENCE_FIELDS.relevanceCriteria) && {
            relevanceCriteria:
              protocolIntelligenceData?.relevanceCriteria ?? '',
          }),
        },
      })
    );
  };

  const clearAllContents = () => {
    dispatch(
      setFormData({
        contextAndCriteria: {
          ...formData.contextAndCriteria,
          issueCodes: [],
          context: '',
          relevanceCriteria: '',
        },
      })
    );
  };

  const {validateNextStep, isNextButtonDisabled, hasSpecialCharError} =
    useStepValidation(
      steps[currentIndex]?.key,
      jobType,
      formData,
      issueCodeTypeJobRef,
      isIssueCodeStepValid,
      isCreatingGroup
    );

  // Step navigation
  const goPrevious = () => {
    setSteps((prev) =>
      prev.map((step, i) =>
        i === currentIndex
          ? {...step, state: 'future'}
          : i === currentIndex - 1
            ? {...step, state: 'current'}
            : step
      )
    );
  };

  const goNext = () => {
    setSteps((prev) =>
      prev.map((step, i) =>
        i === currentIndex
          ? {...step, state: 'completed'}
          : i === currentIndex + 1
            ? {...step, state: 'current'}
            : step
      )
    );
  };

  const goNextWithValidation = () => {
    if (!validateNextStep()) return;
    goNext();
  };

  useEffect(() => {
    const {cloneJobName} = (store.getState() as RootState).aiReview;

    const isCloneFlow = !!cloneJobName;
    const isEditFlow = !!jobId;

    // Fresh CreateJob page
    if (!isCloneFlow && !isEditFlow) {
      dispatch(resetFormData());
      dispatch(resetEditMode());
      dispatch(setDefineCriteria(CRITERIA.MANUAL));

      dispatch(setCloneJobName(''));
    }
  }, []);

  const triggerToast = () => {
    dispatch(
      showToast({
        title: t('ai.review.newJobDetail.draftToastTitle'),
        message: t('ai.review.newJobDetail.draftToastDesc', {
          value: formData?.jobDetail?.jobName,
        }),
        status: [ToastType.Success],
      })
    );
    dispatch(setCloneJobName(''));

    dispatch(resetFormData());
    dispatch(resetEditMode());
    dispatch(setDefineCriteria(CRITERIA.MANUAL));
  };
  const onSaveDraft = () => {
    if (!validateNextStep()) return;

    const issueCodeData = formData.contextAndCriteria.issueCodes.map((ic) => ({
      code: ic.code,
      question: ic.question,
    }));
    const payload = {
      jobType: jobType,
      jobName: formData.jobDetail.jobName,
      description: formData.jobDetail.description ?? '',
      groupID: Number(formData.jobDetail.selectedGroup?.id || -1),
      caseContext: formData.contextAndCriteria.context,
      issueCodes: issueCodeData,
      groundTruth: formData.groundTruth ?? '',
      relevanceCriteria: formData.contextAndCriteria.relevanceCriteria,
      sourceType: formData.sourceSelectionMode,
      sourceID: formData.sourceSelection ?? 0,
    };

    const mutationOptions = {
      onSuccess: async (data: any) => {
        if (data?.errorMessage) {
          dispatch(
            showToast({
              title: t('common.error'),
              message: data.errorMessage,
              status: [ToastType.Error],
            })
          );
          return;
        }
        triggerToast();
        navigate('/AI/Review');
      },
      onError: (error: any) => {
        console.log(error);
        dispatch(
          showToast({
            title: t('common.error'),
            message:
              error?.response?.data?.detail ??
              error?.message ??
              t('common.error'),
            status: [ToastType.Error],
          })
        );
      },
    };

    if (formData.jobId) {
      updateAIReviewJob(
        {
          ...payload,
          jobId: formData.jobId,
        },
        mutationOptions
      );
    } else {
      createAIReviewJob(payload, mutationOptions);
    }
  };

  const onSubmitNewJob = () => setShowNewJobConfirmationModal(true);
  const onCloseNewJobConfirmationModal = () =>
    setShowNewJobConfirmationModal(false);

  const isSaveDraftDisabled = !formData?.jobDetail.jobName;
  const initialFormData = useSelector(
    (state: RootState) => state.aiReview.initialFormData
  );
  const hasUnsavedChanges = !isEqual(formData, initialFormData);
  const lastInsideTargetRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!hasUnsavedChanges || showNewJobConfirmationModal) return;

    const handleOutsideClick = (e: MouseEvent) => {
      if (e.button === 2) return;

      const wizardEl = document.querySelector('.create-job-detail');
      if (!wizardEl) return;

      const target = e.target as HTMLElement;

      // If click is inside wizard, just track it and exit
      if (wizardEl.contains(target)) {
        lastInsideTargetRef.current = target;
        return;
      }

      // If this is a MUI portal AND it was opened from inside the wizard, skip
      const isMuiPortal =
        target.closest('[role="listbox"]') ||
        target.closest('[role="option"]') ||
        target.closest('.MuiPopover-root') ||
        target.closest('.MuiMenu-root') ||
        target.closest('[role="dialog"]') ||
        target.closest('.modal');

      if (isMuiPortal && lastInsideTargetRef.current) return;

      lastInsideTargetRef.current = null;

      e.stopPropagation();
      e.preventDefault();
      setShowExitModal(true);
    };
    document.addEventListener('mousedown', handleOutsideClick, true);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick, true);
    };
  }, [hasUnsavedChanges, showNewJobConfirmationModal]);

  return (
    <div
      className="create-job-detail"
      data-testid={`new-job-detail-stepper-wrapper`}
    >
      <NewJobDetailHeader
        showExitModal={showExitModal}
        setShowExitModal={setShowExitModal}
      />
      <StepperComponent
        steps={steps}
        currentStepKey={steps[currentIndex].key || 1}
        allowNonLinear={false}
        onStepChange={(key) => {
          setSteps((prev) =>
            prev.map((s) =>
              s.key === key
                ? {...s, state: 'current'}
                : s.state === 'current'
                  ? {...s, state: 'completed'}
                  : s
            )
          );
        }}
      />
      <div
        className="stepper-buttons"
        data-testid={`new-job-detail-stepper-buttons`}
      >
        <Box className="stepper-btns-left-container">
          {currentIndex !== 0 && (
            <ArrButton
              size="medium"
              variant="secondary"
              aria-label={t('common.back')}
              dataTestId="previous-step-btn"
              className="previous-step-btn"
              onClick={goPrevious}
              startIcon={
                <Next className="previous-icon" data-testid="previous-icon" />
              }
              tabIndex={0}
            >
              {t('common.back')}
            </ArrButton>
          )}
        </Box>

        <Box className="stepper-btns-right-container">
          <ArrButton
            size="medium"
            variant="secondary"
            aria-label={t('ai.review.newJobDetail.exitJobModalDraftBtn')}
            dataTestId="save-draft-btn"
            className={`save-draft-btn ${isSaveDraftDisabled ? 'Mui-disabled' : ''}`}
            onClick={onSaveDraft}
            isAriaDisabled={isSaveDraftDisabled}
          >
            {formData?.jobId
              ? t('common.save')
              : t('ai.review.newJobDetail.exitJobModalDraftBtn')}
          </ArrButton>

          {currentIndex !== steps.length - 1 && (
            <ArrButton
              dataTestId="next-step-btn"
              className="next-step-btn"
              onClick={goNextWithValidation}
              variant="primary"
              size="medium"
              aria-disabled={isNextButtonDisabled()}
              aria-label={t('common.next')}
              isAriaDisabled={isNextButtonDisabled()}
              endIcon={<Next className="next-icon" data-testid="next-icon" />}
            >
              {t('common.next')}
            </ArrButton>
          )}

          {currentIndex === steps.length - 1 && (
            <>
              <ArrButton
                dataTestId="submit-btn"
                className="submit-btn"
                variant="primary"
                size="medium"
                onClick={onSubmitNewJob}
                aria-label={t('common.submit')}
                aria-disabled={isEmpty(selectedSourceId)}
                isAriaDisabled={isEmpty(selectedSourceId)}
                disabled={isEmpty(selectedSourceId)}
                tabIndex={0}
                endIcon={
                  <Next className="next-icon" data-testid="submit-icon" />
                }
              >
                {t('common.submit')}
              </ArrButton>
              <NewJobConfirmationModal
                showNewJobConfirmationModal={showNewJobConfirmationModal}
                onCloseNewJobConfirmationModal={onCloseNewJobConfirmationModal}
              />
            </>
          )}
        </Box>
      </div>
      <Box className="stepper-content">
        {steps[currentIndex]?.key === 1 ? (
          <JobDetailStep
            data={formData.jobDetail}
            hasSpecialCharError={hasSpecialCharError}
            setData={(data) =>
              dispatch(
                setFormData({
                  jobDetail: data,
                })
              )
            }
            isCreatingGroup={isCreatingGroup}
            setIsCreatingGroup={setIsCreatingGroup}
          />
        ) : steps[currentIndex]?.key === 2 ? (
          <>
            {jobType === AI_REVIEW_JOB_TYPE.issueCoding ? (
              <IssueCodeTypeJob
                ref={issueCodeTypeJobRef}
                hasSpecialCharError={hasSpecialCharError}
                setIsValid={setIsIssueCodeStepValid}
                isProtocolDisabled={isProtocolDisabled}
                handleUseSavedProtocol={handleUseSavedProtocol}
                clearAllContents={clearAllContents}
              />
            ) : (
              <ContextAndCriteriaStep
                isProtocolDisabled={isProtocolDisabled}
                handleUseSavedProtocol={handleUseSavedProtocol}
                clearAllContents={clearAllContents}
              />
            )}
          </>
        ) : steps[currentIndex]?.key === 3 ? (
          <SourceSelectionStep
            onSelectionChange={(id) => {
              setSelectedSourceId(id);
              dispatch(
                setFormData({
                  sourceSelection: Number(id ?? 0),
                })
              );
            }}
          />
        ) : (
          'Content for each step will go here'
        )}
      </Box>
      {toast?.open && toast?.status?.includes(ToastType.Error) && (
        <ArrToastNotification
          title={toast.title}
          message={toast.message}
          status={toast.status}
          onClose={() => dispatch(hideToast())}
          data-testid="ar-review-toast-global"
          aria-label={toast.title}
        />
      )}
    </div>
  );
};

export default NewJobDetail;
